import * as THREE from 'three';

export interface Master3DTargets {
  desiredCamPos: THREE.Vector3;
  desiredLookAt: THREE.Vector3;
  desiredCandlePos: THREE.Vector3;
  desiredWorkshopPos: THREE.Vector3;
  desiredGalleryPos: THREE.Vector3;
  desiredUnboxingPos: THREE.Vector3;
  candleScale: number;
  flameIntensity: number;
  sparkOpacity: number;
  petalOpacity: number;
  lidY: number;
  lidRotZ: number;
}

export interface SceneWindow {
  id: number;
  start: number;
  end: number;
}

// Unified Desktop Scene Windows (0.00 to 1.00) - Strictly Sequential & Disjoint
export const DESKTOP_SCENES: SceneWindow[] = [
  { id: 1, start: 0.00, end: 0.07 },
  { id: 2, start: 0.07, end: 0.14 },
  { id: 3, start: 0.14, end: 0.21 },
  { id: 4, start: 0.21, end: 0.28 },
  { id: 5, start: 0.28, end: 0.35 },
  { id: 6, start: 0.35, end: 0.42 },
  { id: 7, start: 0.42, end: 0.49 },
  { id: 8, start: 0.49, end: 0.56 },
  { id: 9, start: 0.56, end: 0.63 },
  { id: 10, start: 0.63, end: 0.72 },
  { id: 11, start: 0.72, end: 0.80 },
  { id: 12, start: 0.80, end: 0.87 },
  { id: 13, start: 0.87, end: 0.94 },
  { id: 14, start: 0.94, end: 1.00 },
];

// Unified Mobile Scene Windows (0.00 to 1.00)
export const MOBILE_SCENES: SceneWindow[] = [
  { id: 1, start: 0.00, end: 0.07 },
  { id: 2, start: 0.07, end: 0.14 },
  { id: 3, start: 0.14, end: 0.21 },
  { id: 4, start: 0.21, end: 0.28 },
  { id: 5, start: 0.28, end: 0.36 },
  { id: 6, start: 0.36, end: 0.44 },
  { id: 7, start: 0.44, end: 0.52 },
  { id: 8, start: 0.52, end: 0.75 },
  { id: 9, start: 0.75, end: 0.90 },
  { id: 10, start: 0.90, end: 1.00 },
];

/**
 * Calculates continuous scene opacities with quintic Hermite smootherstep easing:
 * f(t) = 6t^5 - 15t^4 + 10t^3 (zero 1st & 2nd derivatives at boundaries prevent sudden jumps & add natural anticipation).
 */
export function getMasterSceneAlpha(
  p: number,
  start: number,
  end: number,
  fadeRatio = 0.20
): number {
  if (start === 0 && p <= 0.001) return 1.0;
  if (p < start || p >= end) return 0;
  const range = end - start;
  const fadeInEnd = start + range * fadeRatio;
  const fadeOutStart = end - range * fadeRatio;

  let rawAlpha = 1;
  if (start > 0 && p < fadeInEnd) {
    rawAlpha = (p - start) / (fadeInEnd - start);
  } else if (p > fadeOutStart) {
    rawAlpha = (end - p) / (end - fadeOutStart);
  }

  rawAlpha = Math.max(0, Math.min(1, rawAlpha));
  return rawAlpha * rawAlpha * rawAlpha * (rawAlpha * (rawAlpha * 6 - 15) + 10);
}

/**
 * Normalizes progress p within range [start, end] to [0, 1].
 */
export function getLocalProgress(p: number, start: number, end: number): number {
  if (p <= start) return 0;
  if (p >= end) return 1;
  return (p - start) / (end - start);
}

/**
 * Quintic Hermite interpolation between two values with zero acceleration at boundaries
 */
function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Pre-allocated static Vector3 instances to avoid per-frame GC allocations
const cachedCamPos = new THREE.Vector3();
const cachedLookAt = new THREE.Vector3();
const cachedCandlePos = new THREE.Vector3();
const cachedWorkshopPos = new THREE.Vector3();
const cachedGalleryPos = new THREE.Vector3();
const cachedUnboxingPos = new THREE.Vector3();

/**
 * Master Animation Timeline Controller for 3D Camera & Scene Transformation
 * Smoothly interpolates positions across the entire scroll spectrum (0.0 - 1.0).
 * Dedicated mobile branch optimizes camera movement and reduces simultaneous math calculations.
 */
export function evaluateMaster3DTimeline(
  p: number,
  elapsedTime: number,
  activeCandleIdx: number,
  isMobile = false
): Master3DTargets {
  const desiredCamPos = cachedCamPos.set(0, 0, 10);
  const desiredLookAt = cachedLookAt.set(0, 0, 0);
  const desiredCandlePos = cachedCandlePos.set(0, 0, 0);
  const desiredWorkshopPos = cachedWorkshopPos.set(0, -20, 0);
  const desiredGalleryPos = cachedGalleryPos.set(0, -20, 0);
  const desiredUnboxingPos = cachedUnboxingPos.set(0, -20, 0);

  let candleScale = 1;
  let flameIntensity = 2.5;
  let sparkOpacity = 0;
  let petalOpacity = 0;
  let lidY = 0.8;
  let lidRotZ = 0;

  // SCENE 1: Darkness -> Spark (0.00 - 0.07)
  if (p < 0.07) {
    const t = getLocalProgress(p, 0.00, 0.07);
    desiredCamPos.set(0, 0, mix(10, 8, t));
    desiredLookAt.set(0, 0, 0);
    sparkOpacity = t;
    flameIntensity = mix(0.5, 2.5, t);
    desiredCandlePos.set(0, mix(-3, -0.2, t), 0);
  }
  // SCENE 2: Ignition & Flame (0.07 - 0.14)
  else if (p < 0.14) {
    const t = getLocalProgress(p, 0.07, 0.14);
    desiredCandlePos.set(0, mix(-0.2, 0, t), 0);
    if (isMobile) {
      desiredCamPos.set(0, mix(0, 0.2, t), mix(8, 4.5, t));
      flameIntensity = 2.5 + Math.sin(elapsedTime * 4) * 0.2;
    } else {
      desiredCamPos.set(mix(0, Math.sin(t * Math.PI) * 2, t), mix(0, 0.2, t), mix(8, 4.2, t));
      flameIntensity = 2.5 + Math.sin(elapsedTime * 8) * 0.4;
    }
    desiredLookAt.set(0, 0, 0);
  }
  // SCENE 3: Logo & Title (0.14 - 0.21)
  else if (p < 0.21) {
    desiredCamPos.set(0, 0.2, isMobile ? 4.5 : 4.2);
    desiredLookAt.set(0, 0, 0);
    desiredCandlePos.set(0, 0, 0);
    flameIntensity = 2.5 + Math.sin(elapsedTime * 6) * 0.3;
  }
  // SCENE 4: Flying into Flame (0.21 - 0.28)
  else if (p < 0.28) {
    const t = getLocalProgress(p, 0.21, 0.28);
    desiredCamPos.set(0, mix(0.2, 0.8, t), mix(isMobile ? 4.5 : 4.2, 0.4, t));
    desiredLookAt.set(0, mix(0, 0.8, t), 0);
    desiredCandlePos.set(0, 0, 0);
    flameIntensity = mix(2.5, 3.5, t);
  }
  // SCENE 5: Inside Molten Core (0.28 - 0.35)
  else if (p < 0.35) {
    const t = getLocalProgress(p, 0.28, 0.35);
    if (isMobile) {
      desiredCamPos.set(0, mix(0.8, 0.2, t), mix(0.4, 1.2, t));
      desiredLookAt.set(0, mix(0.8, 0, t), 0);
    } else {
      const orbitX = Math.sin(t * Math.PI * 2) * 1.5;
      const orbitZ = Math.cos(t * Math.PI * 2) * 1.5;
      desiredCamPos.set(mix(0, orbitX, Math.min(1, t * 2)), mix(0.8, 0.2, t), mix(0.4, orbitZ, Math.min(1, t * 2)));
      desiredLookAt.set(0, mix(0.8, 0, t), 0);
    }
    desiredCandlePos.set(0, 0, 0);
    flameIntensity = mix(3.5, 2.5, t);
  }
  // SCENE 6: Rashmi's Workshop (0.35 - 0.42)
  else if (p < 0.42) {
    const t = getLocalProgress(p, 0.35, 0.42);
    desiredWorkshopPos.set(0, mix(-20, 0, t), 0);
    desiredCandlePos.set(0, mix(0, -15, t), 0);
    if (isMobile) {
      desiredCamPos.set(0, mix(0.2, 1.2, t), mix(1.2, 3.8, t));
    } else {
      desiredCamPos.set(mix(0, -3, t), mix(0.2, 1.5, t), mix(1.5, 3.5, t));
    }
    desiredLookAt.set(0, mix(0, -0.5, t), 0);
  }
  // SCENE 7: Floating Ingredients (0.42 - 0.50)
  else if (p < 0.50) {
    const t = getLocalProgress(p, 0.42, 0.50);
    desiredWorkshopPos.set(0, mix(0, -20, t), 0);
    desiredCandlePos.set(0, -15, 0);
    if (isMobile) {
      desiredCamPos.set(0, mix(1.2, 0, t), mix(3.8, 4.8, t));
    } else {
      const orbitX = Math.sin(t * Math.PI * 2) * 5;
      const orbitZ = Math.cos(t * Math.PI * 2) * 5;
      desiredCamPos.set(mix(-3, orbitX, Math.min(1, t * 2)), mix(1.5, 0, t), mix(3.5, orbitZ, Math.min(1, t * 2)));
    }
    desiredLookAt.set(0, mix(-0.5, 0, t), 0);
  }
  // SCENE 8: Assembly into Finished Candle (0.50 - 0.57)
  else if (p < 0.57) {
    const t = getLocalProgress(p, 0.50, 0.57);
    desiredWorkshopPos.set(0, -20, 0);
    desiredCandlePos.set(0, mix(-15, 0, t), 0);
    candleScale = 1;
    if (isMobile) {
      desiredCamPos.set(0, mix(0, 0.5, t), mix(4.8, 5.2, t));
    } else {
      desiredCamPos.set(0, mix(0, 0.5, t), mix(5, 4.5, t));
    }
    desiredLookAt.set(0, 0, 0);
  }
  // SCENE 9: Scent Realms (0.57 - 0.65)
  else if (p < 0.65) {
    const t = getLocalProgress(p, 0.57, 0.65);
    petalOpacity = Math.sin(t * Math.PI);
    if (isMobile) {
      desiredCamPos.set(0, mix(0.5, 0.3, t), mix(5.2, 5.0, t));
    } else {
      desiredCamPos.set(mix(0, Math.sin(t * 4) * 2, t), mix(0.5, 0.3, t), mix(4.5, 4.5 + Math.cos(t * 4) * 0.5, t));
    }
    desiredLookAt.set(0, 0, 0);
    desiredCandlePos.set(0, 0, 0);
  }
  // SCENE 10: Masterpiece Gallery (0.65 - 0.74)
  else if (p < 0.74) {
    const t = getLocalProgress(p, 0.65, 0.74);
    desiredCandlePos.set(0, mix(0, -15, t), 0);
    desiredGalleryPos.set(-activeCandleIdx * (isMobile ? 4.2 : 5.0), mix(-20, 0, t), 0);
    if (isMobile) {
      desiredCamPos.set(0, 0.3, mix(5.0, 5.2, t));
    } else {
      desiredCamPos.set(mix(-1.51, 0, t), 0.3, mix(4.17, 4.5, t));
    }
    desiredLookAt.set(0, 0, 0);
  }
  // SCENE 11: Living Spaces Interiors (0.74 - 0.82)
  else if (p < 0.82) {
    const t = getLocalProgress(p, 0.74, 0.82);
    desiredGalleryPos.set(-activeCandleIdx * (isMobile ? 4.2 : 5.0), mix(0, -20, t), 0);
    desiredCandlePos.set(0, mix(-15, 0, t), 0);
    candleScale = 1;
    if (isMobile) {
      desiredCamPos.set(0, mix(0.3, 0.4, t), mix(5.2, 4.5, t));
    } else {
      desiredCamPos.set(mix(0, 1.5, t), mix(0.3, 0.4, t), mix(4.5, 3.8, t));
    }
    desiredLookAt.set(0, 0, 0);
  }
  // SCENE 12: Unboxing Experience (0.82 - 0.89)
  else if (p < 0.89) {
    const t = getLocalProgress(p, 0.82, 0.89);
    desiredCandlePos.set(0, mix(0, -15, t), 0);
    desiredUnboxingPos.set(0, mix(-20, 0, t), 0);
    lidY = 0.8 + t * 1.5;
    lidRotZ = t * (isMobile ? 0.15 : 0.3);
    if (isMobile) {
      desiredCamPos.set(0, mix(0.4, 1.2, t), mix(4.5, 4.8, t));
    } else {
      desiredCamPos.set(mix(1.5, 0, t), mix(0.4, 1.2, t), mix(3.8, 4.0, t));
    }
    desiredLookAt.set(0, 0, 0);
  }
  // SCENE 13: Handwritten Reviews (0.89 - 0.95)
  else if (p < 0.95) {
    const t = getLocalProgress(p, 0.89, 0.95);
    desiredUnboxingPos.set(0, mix(0, -20, t), 0);
    desiredCandlePos.set(0, mix(-15, 0, t), 0);
    if (isMobile) {
      desiredCamPos.set(0, mix(1.2, 0, t), mix(4.8, 5.5, t));
    } else {
      desiredCamPos.set(0, mix(1.2, 0, t), mix(4.0, 5.0, t));
    }
    desiredLookAt.set(0, 0, 0);
  }
  // SCENE 14: Final Warmth (0.95 - 1.00)
  else {
    desiredCandlePos.set(0, 0, 0);
    desiredCamPos.set(0, 0, isMobile ? 5.5 : 5.0);
    desiredLookAt.set(0, 0, 0);
  }

  return {
    desiredCamPos,
    desiredLookAt,
    desiredCandlePos,
    desiredWorkshopPos,
    desiredGalleryPos,
    desiredUnboxingPos,
    candleScale,
    flameIntensity,
    sparkOpacity,
    petalOpacity,
    lidY,
    lidRotZ,
  };
}
